import { t } from "i18next";
import {
  Color,
  DoubleSide,
  Group,
  Intersection,
  LineBasicMaterial,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Texture,
  Vector3,
} from "three";
import { Font } from "three/examples/jsm/loaders/FontLoader";
import font from "../../../fonts/RoundedMplus1c_Regular.json";
import * as Config from "../config/ViewerConfig";
import { AutoAttenuablePin } from "../object/AutoAttenuablePin";
import { AutoAttenuatableSphere } from "../object/AutoAttenuatableSphere";
import { AutoAttenuatableTextSprite } from "../object/AutoAttenuatableTextSprite";
import { ExtendedLineLoop } from "../object/ExtendedLineLoop";
import { ExtendedMesh } from "../object/ExtendedMesh";
import { IVolumeInfo } from "../pointcloud/HeightMapHandler";
import { IPointColorRangeInfo } from "../pointcloud/PointCloudTile";
import { triangulateXY } from "../utils/utils";
import { Viewer } from "../viewer";
import {
  ICreateVolumeMeasurementParams,
  IMeasurementHandler,
  IMeasurementVisibleParams,
  MeasurementMode,
  MeasurementState,
  MeasurementType,
} from "./types";
import { getRoundRect } from "./utils";

export interface IVolumeCalculationMeasurement extends IMeasurementHandler {
  pointColorRangeInfo: IPointColorRangeInfo;
  polygonMesh: { vertices: Vector3[]; indices: number[] };
  heatMapVisibility: boolean;
}
export class VolumeCalculationMeasurement
  implements IVolumeCalculationMeasurement
{
  type: MeasurementType = MeasurementType.VOLUME_CALCULATION;
  _id: number;
  _points: Vector3[];
  value: IVolumeInfo;

  private _group: Group;

  private _viewer: Viewer;
  private _mode: MeasurementMode;

  private _pins: AutoAttenuablePin[];
  // private _lineGeometry: BufferGeometry;
  private _lineMaterial: LineBasicMaterial;
  private _line: ExtendedLineLoop;

  private _meshIndices: number[];
  // private _meshGeometry: BufferGeometry;
  private _meshMaterial: MeshBasicMaterial;
  private _mesh: ExtendedMesh;
  private _sphereMaterial: MeshBasicMaterial;
  private _spheres: AutoAttenuatableSphere[];

  private _font: Font;
  private _textSprite: AutoAttenuatableTextSprite;

  private _textTextureContext: CanvasRenderingContext2D;
  private _textTexture: Texture;

  private _params: ICreateVolumeMeasurementParams;

  private _pointColorRangeInfo: IPointColorRangeInfo;

  private _isHeatMapVisible: boolean;
  public appearanceState: MeasurementState = "default";

  // private _height: number;

  public get id(): number {
    return this._id;
  }

  public get heatMapVisibility() {
    return this._isHeatMapVisible;
  }

  public set heatMapVisibility(visibility: boolean) {
    this.setMeshVisibility(!visibility);
    this._isHeatMapVisible = visibility;
  }

  public get pointColorRangeInfo(): IPointColorRangeInfo {
    return this._pointColorRangeInfo;
  }

  public get polygonMesh(): { vertices: Vector3[]; indices: number[] } {
    return { vertices: this._points, indices: this._meshIndices };
  }

  constructor(
    viewer: Viewer,
    params: ICreateVolumeMeasurementParams,
    textScale: Config.ITextScale
  ) {
    this._viewer = viewer;
    this._params = params;
    this.value = {
      cutVolume: 0,
      fillVolume: 0,
      maxHeightOffset: 0,
      minHeightOffset: 0,
    };
    this._meshIndices = [];
    // this._height = 0;
    this._points = [];
    this._group = new Group();
    this._viewer.getScene(Config.SCENE_TYPE.MEASUREMENT).add(this._group);
    this._mode = MeasurementMode.CREATE;

    // this._lineGeometry = new BufferGeometry();
    this._lineMaterial = new LineBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      linewidth: 3,
      depthTest: false,
    });
    this._line = new ExtendedLineLoop(this._lineMaterial);
    this._line.visible = false;
    this._group.add(this._line);

    // this._meshGeometry = new BufferGeometry();
    this._meshMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
      side: DoubleSide,
      opacity: 0.5,
      transparent: true,
    });
    this._mesh = new ExtendedMesh(this._meshMaterial);
    this._group.add(this._mesh);

    this._pins = [];
    // this._sphereGeometry = new SphereBufferGeometry(1, 32, 16);
    this._sphereMaterial = new MeshBasicMaterial({
      color: Config.NORMAL_MEASUREMENT_COLOR,
    });
    this._spheres = [];

    this._font = new Font(font);

    const textCanvas = document.createElement("canvas");
    textCanvas.width = 1200;
    textCanvas.height = 700;
    this._textTextureContext = textCanvas.getContext("2d");
    this._textTexture = new Texture(textCanvas);
    this._textTexture.needsUpdate = true;
    this._textSprite = new AutoAttenuatableTextSprite(
      this._textTexture,
      textScale
    );
    this._textSprite.visible = false;
    this._group.add(this._textSprite);
  }
  hitText(ray: Raycaster): Intersection[] {
    const objects = [this._textSprite, this._mesh];
    objects.forEach((object) => {
      object.geometry.computeBoundingBox();
      object.geometry.computeBoundingSphere();
    });
    return ray.intersectObjects(objects, true);
  }

  private updateTextContext2d(state: MeasurementState) {
    this.appearanceState = state;
    const canvasWidth = this._textTextureContext.canvas.width;
    const canvasHeight = this._textTextureContext.canvas.height;
    this._textSprite.geometry.computeBoundingBox();
    this._textTextureContext.clearRect(0, 0, canvasWidth, canvasHeight);

    const borderThickness = 4;
    const fontsize = 44;
    const fontface = "Arial";
    const tab1Width = 16;
    const tab2Width = 30;
    const edgeConst = 1.05;

    interface TextData {
      label: string;
      value: string;
    }

    const excavationText: TextData = {
      label: `${t("EXCAVATION")}`,
      value: `${this.value.fillVolume.toFixed(3)} m³`,
    };
    const embankmentText: TextData = {
      label: `${t("EMBANKMENT")}`,
      value: `${this.value.cutVolume.toFixed(3)} m³`,
    };
    const soilVolumeDifferenceText: TextData = {
      label: `${t("SOIL_VOLUME_DIFFERENCE")}`,
      value: `${(this.value.fillVolume - this.value.cutVolume).toFixed(3)} m³`,
    };
    const texts: any[] = [
      excavationText,
      embankmentText,
      soilVolumeDifferenceText,
    ];

    this._textTextureContext.font = fontsize + "px " + fontface;

    const textHeight = fontsize;

    let maxLabelWidth: number = 0;
    let maxValueWidth: number = 0;
    for (let i = 0; i < texts.length; i++) {
      let _ = this._textTextureContext.measureText(texts[i].label).width;
      if (maxLabelWidth < _) {
        maxLabelWidth = _;
      }
      _ = this._textTextureContext.measureText(texts[i].value).width;
      if (maxValueWidth < _) {
        maxValueWidth = _;
      }
    }

    this._textTextureContext.lineWidth = borderThickness;
    this._textTextureContext.fillStyle = {
      default: Config.DEFAULT_MEASUREMENT_TEXT_BGCOLOR,
      hovered: Config.HOVERED_MEASUREMENT_TEXT_BGCOLOR,
      selected: Config.SELECTED_MEASUREMENT_TEXT_BGCOLOR,
    }[state];

    this._textTextureContext.strokeStyle = "rgba(0,0,0,1.0)"; //
    const roundedRectWidth =
      tab1Width +
      maxLabelWidth +
      tab2Width +
      maxValueWidth +
      borderThickness * 2;
    const roundedRectHeight = textHeight * texts.length + borderThickness * 2;
    getRoundRect(
      this._textTextureContext,
      borderThickness / 2,
      borderThickness / 2,
      roundedRectWidth,
      roundedRectHeight,
      6
    );

    this._textTextureContext.fillStyle = "rgba(255,255,255,1.0)"; //text color
    this._textTextureContext.strokeStyle = "rgba(255,255,255,1.0)"; //text color

    for (let i = 0; i < texts.length; i++) {
      this._textTextureContext.fillText(
        texts[i].label,
        tab1Width,
        textHeight * (i + 1)
      );
      this._textTextureContext.fillText(
        texts[i].value,
        // 右揃え
        roundedRectWidth -
          this._textTextureContext.measureText(texts[i].value).width -
          borderThickness,
        textHeight * (i + 1)
      );
    }

    // テキストテクスチャが間違いなく収まる幅へ定数で調整
    const newCanvasWidth = Math.ceil(roundedRectWidth * edgeConst);
    const newCanvasHeight = Math.ceil(roundedRectHeight * edgeConst);

    // 他の計測における1行のテキストボックスと文字の大きさを合わせるため，スケーリングを調整
    const oneLineCanvasWidth = Math.ceil(
      (tab1Width + maxValueWidth + borderThickness * 2) * edgeConst
    );
    const oneLineCanvasHeight = Math.ceil(
      (textHeight + borderThickness * 2) * edgeConst
    );
    const diagonalConst = Math.sqrt(
      (newCanvasWidth ** 2 + newCanvasHeight ** 2) /
        (oneLineCanvasWidth ** 2 + oneLineCanvasHeight ** 2)
    );
    this._textSprite.adjustScaleConst(diagonalConst);

    const geom = this._textSprite.geometry;
    const uvs = geom.attributes["uv"];
    uvs.setXY(0, 0, 1 - newCanvasHeight / canvasHeight);
    uvs.setXY(
      1,
      newCanvasWidth / canvasWidth,
      1 - newCanvasHeight / canvasHeight
    );
    uvs.setXY(2, newCanvasWidth / canvasWidth, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._textSprite.aspect = newCanvasHeight / newCanvasWidth;

    this._textTexture.needsUpdate = true;
  }

  private calculateVolume() {
    this._meshIndices = triangulateXY(this._points);
    this.value = this._viewer.calculateVolumeInfo(
      this._points,
      this._meshIndices
    );
  }

  public setPoints(points: Vector3[]) {
    this._points = points.map((p) => p.clone());
    // this._height = 0;
    // for(const p of this._points){
    //   this._height += p.z;
    // }
    // this._height /= this._points.length; // averaging
    this._id =
      this._id === undefined ? this._viewer.generateMeasurementId() : this._id;
    // this._mode = MeasurementMode.INACTIVE;
    this.disposeSpheres();
    this.disposePins();
    for (const point of this._points) {
      const sphere = new AutoAttenuatableSphere(
        this._sphereMaterial,
        Config.MEASUREMENT_SPHERE_SIZE
      );
      sphere.position.copy(point);
      this._group.add(sphere);
      this._spheres.push(sphere);
    }

    for (let i = 0; i < this._points.length; ++i) {
      const pin = new AutoAttenuablePin(`${i}`, this._viewer);
      pin.setPosition(this._points[i]);
      this._group.add(pin);
      this._pins.push(pin);
    }

    this.calculateVolume();
    // this.onUpdateComplete();
    this.executeHeatMap();
    this.render();
  }

  private disposeSpheres() {
    for (let sphere of this._spheres) {
      // this._group.remove(sphere);
      sphere.dispose();
      sphere = undefined;
    }
    this._spheres.length = 0;
  }

  private disposePins() {
    for (let pin of this._pins) {
      this._group.remove(pin);
      pin.geometry.dispose();
      pin.geometry = undefined;
      pin.material.dispose();
      pin.material = undefined;
      pin = undefined;
    }
    this._pins.length = 0;
  }

  public dispose() {
    this._points.length = 0;

    this._group.remove(this._mesh);
    this._group.remove(this._line);
    this._group.remove(this._textSprite);

    // this._meshGeometry.dispose();
    // this._meshGeometry = undefined;
    this._meshMaterial.dispose();
    this._meshMaterial = undefined;
    this._mesh = undefined;

    // this._lineGeometry.dispose();
    // this._lineGeometry = undefined;
    this._lineMaterial.dispose();
    this._lineMaterial = undefined;
    this._line = undefined;

    // this._sphereGeometry.dispose();
    this.disposePins();
    this.disposeSpheres();
    this._sphereMaterial.dispose();
    // this._sphereGeometry = undefined;
    this._sphereMaterial = undefined;

    this._textSprite.dispose();
    this._textSprite = undefined;

    this._viewer.updateRenderer();
  }

  public render() {
    this._line.prepareRendering(this._points);
    this._line.visible = true;
    this._mesh.prepareRendering(this._points, this._meshIndices);

    for (const pin of this._pins) {
      pin.visible = false;
    }

    for (const sphere of this._spheres) {
      sphere.visible = false;
    }

    switch (this._mode) {
      case MeasurementMode.EDIT:
        this._lineMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        this._meshMaterial.color.setHex(Config.EDIT_MEASUREMENT_COLOR);
        this._points.forEach((val, i) => this._pins[i].setPosition(val));
        for (const pin of this._pins) {
          pin.visible = true;
        }
        break;
      default:
        this._lineMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);
        this._meshMaterial.color.setHex(Config.NORMAL_MEASUREMENT_COLOR);

        this._points.forEach((val, i) => this._spheres[i].position.copy(val));
        for (const sphere of this._spheres) {
          sphere.visible = true;
        }
    }

    if (this._points.length > 2) {
      this.updateTextContext2d(this.appearanceState);
      const textPosition = new Vector3(0, 0, 0);
      for (const point of this._points) {
        textPosition.x += point.x;
        textPosition.y += point.y;
        textPosition.z += point.z;
      }
      textPosition.multiplyScalar(1 / this._points.length);

      this._textSprite.setPosition(textPosition);
      this._textSprite.visible = true;
    }
    this._viewer.updateRenderer();
  }

  public switchMode(mode: MeasurementMode) {
    this._mode = mode;
    this.render();
  }

  public onPositionUpdate(id: number, position: Vector3) {
    this._points[id].copy(position);
    // this._height = position.z;
    // for(const p of this._points){
    //   p.setZ(this._height);
    // }

    this.calculateVolume();
    this.render();
  }

  private executeHeatMap() {
    const result = this._viewer.calculateVolumeInfo(
      this._points,
      this._meshIndices
    );

    // console.log("volumeInfo", JSON.stringify(result));
    this._pointColorRangeInfo = {
      lower: {
        min: {
          color: new Color(this._params.lowerBound.minColor),
          val: 0,
        },
        max: {
          color: new Color(this._params.lowerBound.maxColor),
          val: result.minHeightOffset,
        },
      },
      upper: {
        min: {
          color: new Color(this._params.upperBound.minColor),
          val: 0,
        },
        max: {
          color: new Color(this._params.upperBound.maxColor),
          val: result.maxHeightOffset,
        },
      },
    };

    // this._viewer.applyHeightBasedColor(
    //   this.polygonMesh,
    //   this._pointColorRangeInfo
    // );
  }

  public onUpdateComplete(id?: number, position?: Vector3) {
    this._viewer.setPointsToMeasurement(this._id, this._points);
  }

  public getPickableObjects(): Object3D[] {
    return this._pins;
  }

  public setVisibility(params: IMeasurementVisibleParams) {
    if (params.text !== undefined) {
      this._textSprite.visible = params.text;
    }
    if (params.measurement !== undefined) {
      this._group.visible = params.measurement;
      // this._viewer.setHeatMapVisibility(this.id, this._isHeatMapVisible);
    }
    this._viewer.updateRenderer();
  }

  public setMeshVisibility(visible: boolean) {
    this._mesh.visible = visible;
    this._viewer.updateRenderer();
  }

  public getVisibility(): boolean {
    return this._group.visible;
  }

  public changeAppearanceState(state: MeasurementState): void {
    for (const sphere of this._spheres) {
      sphere.setMaterialByState(state);
      sphere.setGeometryByState(state);
    }

    this._line.setMaterialByState(state);
    this._mesh.setMaterialByState(state);

    this.updateTextContext2d(state);

    this._textSprite.changeRenderOrderByState(state);
  }
}
